import { Component, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ModalController, Platform, ToastController } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { CredentialsService } from '@app/core/authentication/credentials.service';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { Logger } from '@app/core/logger.service';
import { UtilitiesService } from '@app/shared/services/utilities/utilities.service';
import { HttpHeaders, HttpService } from '@app/shared/services/http/http.service';
import { AnalyticsService } from '@app/shared/services/analytics/analytics.service';
import { IonLoaderService } from '@app/shared/services/ion-loader/ion-loader.service';
import { Storage } from '@ionic/storage-angular';
import { App } from '@capacitor/app';
import { untilDestroyed } from '@app/core/until-destroyed';
import { environment } from '@env/environment';

const log = new Logger('LoginComponent');

interface LoginResponse {
  value: boolean;
  status?: string;
}

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnDestroy {
  loginForm!: FormGroup;
  isLoading = false;
  subscription: Subscription;
  counter: number = 0;
  loginAttempts: number = 0;
  showPassword: boolean = false;

  constructor(
    private aRoute: ActivatedRoute,
    private router: Router,
    private formBuilder: FormBuilder,
    private platform: Platform,
    private authServe: AuthenticationService,
    private credService: CredentialsService,
    private utilities: UtilitiesService,
    private http: HttpService,
    private toastCtrl: ToastController,
    private analyticsFB: AnalyticsService,
    private storage: Storage,
    private modalCtrl: ModalController,
    private loader: IonLoaderService
  ) {
    try {
      this.storage.clear()
    } catch (error) {
      this.storage.create().then(() => {
        this.storage.clear()
      });
    }
    this.createForm();
    this.aRoute.params.pipe(untilDestroyed(this)).subscribe(params => {
      if (params && params.user) {
        this.loginForm.get('email').setValue(String(params.user).toLowerCase().trim());
      }
    });
  }

  async ionViewDidEnter() {
    try {
      await this.authServe.userInAccountActivationProcces();
    } catch (error) {
      log.error(error);
    }
    this.subscription = this.platform.backButton.pipe(untilDestroyed(this)).subscribe(() => {
      if (this.counter == 0) {
        this.counter++;
        this.presentToast();
        setTimeout(() => {
          this.counter = 0;
        }, 3000);
      } else {
        App.exitApp();
      }
    });
  }

  ionViewWillLeave() {
    this.subscription.unsubscribe();
  }

  closeModal() {
    this.modalCtrl.dismiss();
  }

  analyticsClickEvent(eventName: string, params: {}) {
    const cred: any = this.credService.credentials; // Credentials App
    const route_page_url: any = this.router.routerState.snapshot.url; // Page route
    this.analyticsFB.logEvent(eventName, params, {
      credentials: cred,
      page_data: { route: route_page_url, title: document.title }
    }); // Analytics LOGS
  }


  async presentToast() {
    const toast = await this.toastCtrl.create({
      message: 'Presione nuevamente para salir.',
      duration: 3000
    });
    toast.present();
  }

  async login() {
    this.isLoading = true;
    await this.loader.ionLoaderPresent();
    const email = String(this.loginForm.get('email').value)
      .toLowerCase()
      .trim();
    try {
      const resp = await this.authServe.signInWithEmailAndPassword(
        email,
        this.loginForm.get('password').value
      );
      if (resp && resp.success && resp.success === true) {
        await this.closeModal();
        this.router.navigate([(this.aRoute.snapshot as any)._routerState.url || this.aRoute.snapshot.queryParams.redirect || '/'], { replaceUrl: true });
      } else {
        await this.loginErrorHandling(resp.data, email);
      }
    } catch (error: any) {
      const code = error && error.code ? String(error.code) : '';
      await this.loginErrorHandling(code, email);
    } finally {
      this.isLoading = false;
      await this.loader.ionLoaderDismiss();
    }
  }

  private async isDocumentValidationInProgress(email: string): Promise<LoginResponse> {
    const headers: HttpHeaders = {
      'Content-Type': 'application/json',
      'private-key': environment.CUSTOM_SECURITY.private_key,
      'public-key': environment.CUSTOM_SECURITY.public_key
    };

    try {
      const user = await this.http.post(
        `${environment.FIREBASE_API}getAuthUserByEmailRequest`,
        {
          email,
          info: true,
          getValidationStatus: true
        },
        { headers }
      );
      const userInformation = user?.userInformation;
      const requestStatus = String(userInformation?.validatedFileRequestStatus || '').toLowerCase();
      return {
        value: (requestStatus === 'pending' || requestStatus === 'requested') && userInformation?.validatedStatus === 'pending',
        status: requestStatus
      }
    } catch (error) {
      log.error('No se pudo consultar el estado de validación de documentos:', error);
      return {
        value: false,
        status: undefined
      }
    }
  }

  private async loginErrorHandling(code: string, email: string): Promise<void> {
    switch (code) {
      case 'auth/user-not-found':
        await this.utilities.presentAlert(
          'La cuenta no existe, por favor verifique que su email sea correcto.',
          'La cuenta no existe'
        );
        break;
      case 'auth/wrong-password':
        this.loginAttempts++;
        if (this.loginAttempts > 1) {
          await this.utilities.presentAlert(
            'Contraseña incorrecta, por favor intente de nuevo.  Si no recuerda su contraseña solicite una recuperación para evitar que su cuenta sea bloqueada.',
            'Contraseña incorrecta'
          );
        } else {
          await this.utilities.presentAlert('Contraseña incorrecta, por favor intente de nuevo.', 'Contraseña incorrecta');
        }
        break;
      case 'auth/invalid-email':
        await this.utilities.presentAlert(
          'El correo electrónico no tiene un formato válido. Verifíquelo e inténtelo de nuevo.',
          'Email inválido'
        );
        break;
      case 'auth/user-disabled':
        const isDocumentValidationInProgress: LoginResponse = await this.isDocumentValidationInProgress(email)
        if (isDocumentValidationInProgress.value) {
          if (isDocumentValidationInProgress.status === 'requested') {
            await this.utilities.presentAlert(
              'Hemos revisado el documento que proporcionó durante su registro como comprobante de su formación académica, sin embargo no cumple con los criterios de validación, por lo que su cuenta está tempralmente inactiva.\n <br> Le enviamos un correo electrónico con las instrucciones para enviar un nuevo documento. Una vez validado su cuenta será reactivada. Si tiene dudas envíe escríbanos a contacto@conectimed.com',
              'Acción Requerida Documento de Registro No Válido'
            );
          }

          if (isDocumentValidationInProgress.status === 'pending') {
            await this.utilities.presentAlert(
              'Su cuenta se encuentra temporalmente inactiva. \n <br>La revisión y validación del nuevo documento que envió está en proceso. Le notificaremos vía email cuando concluya la validación. \n <br>Si requiere ayuda escríbanos a contacto@conectimed.com',
              'Validación de documento en proceso'
            );
          }
        } else {
          await this.utilities.presentAlert(
            'Su cuenta está desactivada. Para recibir ayuda, envíe un correo a contacto@conectimed.com.',
            'Cuenta desactivada'
          );
        }
        break;
      case 'auth/too-many-requests':
        await this.utilities.presentAlert(
          'Ha tenido muchos intentos fallidos de inicio de sesión, por seguridad su cuenta se ha bloqueado. Espere 15 minutos e intente de nuevo.',
          'Limite intentos superados'
        );
        break;
      default:
        await this.utilities.presentAlert(
          'Ha ocurrido un error. Revise su conexión a internet e inténtelo de nuevo. Si el problema persiste, envíe un correo a contacto@conectimed.com.',
          'Error al ingresar'
        );
        break;
    }
  }

  private createForm() {
    this.loginForm = this.formBuilder.group({
      email: [
        '',
        [
          Validators.required,
          Validators.pattern(
            /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          )
        ]
      ],
      password: ['', [Validators.required, Validators.minLength(6)]],
      remember: true
    });
  }

  public noWhiteSpaces(ev: any, field: string) {
    const white = new RegExp(/\s/g);
    if (white.test(String(ev.target.value || '')) === true) {
      const newString = String(ev.target.value || '').replace(/\s/g, '');
      this.loginForm.get(field).setValue(newString);
    }
  }

  ngOnDestroy() {
    // Empty but required by untilDestroyed
  }
}